import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { APIProvider, Map as GMap, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { MapPin, Radio } from "lucide-react";
import { IDS } from "../constants/testIds";
import PTTButton from "./PTTButton";

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

function initials(name) { return name.split(" ").map(s => s[0]).slice(0, 2).join(""); }

function markerHtml(ct, inAlert) {
  const ring = inAlert ? "#DC2626" : ct.online ? "#16A34A" : "#94A3B8";
  const img = ct.avatar_url
    ? `<img src="${ct.avatar_url}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:999px"/>`
    : `<span>${initials(ct.name)}</span>`;
  return `<div class="unit-marker ${inAlert ? "sos" : ""}" style="--ring:${ring}">
    <div class="unit-avatar">${img}</div>
    <div class="unit-label">${ct.name.split(" ")[0]}</div>
  </div>`;
}

function FitOnce({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || points.length === 0) return;
    done.current = true;
    map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [30, 30], maxZoom: 14 });
  }, [map, points]);
  return null;
}

function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function Trails({ units, alertIds, enabled }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useEffect(() => {
    const h = () => setZoom(map.getZoom());
    map.on("zoomend", h);
    return () => map.off("zoomend", h);
  }, [map]);
  if (!enabled || zoom < 14) return null;
  return units.filter(u => (u.trail || []).length > 1).map(u => (
    <Polyline key={u.id} positions={u.trail} pathOptions={{ color: alertIds.has(u.id) ? "#DC2626" : u.online ? "#2563EB" : "#94A3B8", weight: 3, opacity: 0.7, dashArray: "1 6", lineCap: "round" }} />
  ));
}

function UnitPopup({ ct, inAlert, activeDirectId, txStatus, onPTTStart, onPTTEnd }) {
  const status = activeDirectId === ct.id ? txStatus : "idle";
  return (
    <div className="flex items-center gap-3 min-w-[190px]" data-testid={IDS.map.marker(ct.id)}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{ct.name}</div>
        <div className="text-[11px] text-slate-500">{ct.role} · {inAlert ? "SOS ACTIVE" : ct.online ? "Online" : "Offline"}</div>
        <div className="font-mono text-[10px] text-slate-400">{ct.lat?.toFixed(4)}, {ct.lng?.toFixed(4)}</div>
      </div>
      <PTTButton testId={IDS.map.pttMarker(ct.id)} label={`Direct PTT to ${ct.name}`} color={status === "transmitting" ? "green" : "slate"} status={status} size="sm" disabled={!ct.online} onPressStart={() => onPTTStart(ct)} onPressEnd={() => onPTTEnd(ct)} />
    </div>
  );
}

function OsmMap({ units, alertIds, showTrails, ...ptt }) {
  const icons = useMemo(() => Object.fromEntries(units.map(ct => [ct.id, L.divIcon({ className: "", html: markerHtml(ct, alertIds.has(ct.id)), iconSize: [40, 52], iconAnchor: [20, 20], popupAnchor: [0, -18] })])), [units, alertIds]);
  return (
    <MapContainer center={[DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]} zoom={13} className="h-full w-full" zoomControl={true} attributionControl={true}>
      <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
      <FitOnce points={units} />
      <InvalidateOnResize />
      <Trails units={units} alertIds={alertIds} enabled={showTrails} />
      {units.map(ct => (
        <Marker key={ct.id} position={[ct.lat, ct.lng]} icon={icons[ct.id]}>
          <Popup closeButton={false}><UnitPopup ct={ct} inAlert={alertIds.has(ct.id)} {...ptt} /></Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

function GoogleUnits({ units, alertIds, selected, setSelected, ...ptt }) {
  const sel = units.find(u => u.id === selected);
  return (
    <>
      {units.map(ct => (
        <AdvancedMarker key={ct.id} position={{ lat: ct.lat, lng: ct.lng }} onClick={() => setSelected(ct.id)}>
          <div dangerouslySetInnerHTML={{ __html: markerHtml(ct, alertIds.has(ct.id)) }} />
        </AdvancedMarker>
      ))}
      {sel && (
        <InfoWindow position={{ lat: sel.lat, lng: sel.lng }} pixelOffset={[0, -24]} onCloseClick={() => setSelected(null)}>
          <UnitPopup ct={sel} inAlert={alertIds.has(sel.id)} {...ptt} />
        </InfoWindow>
      )}
    </>
  );
}

export default function LiveMap({ contacts, alerts = [], settings, activeDirectId, txStatus, onPTTStart, onPTTEnd, selected, setSelected }) {
  const units = contacts.filter(c => c.lat != null && c.lng != null);
  const alertIds = useMemo(() => new Set(alerts.filter(a => !a.acknowledged).map(a => a.contact_id)), [alerts]);
  const useGoogle = settings?.map_provider === "google" && settings?.google_maps_key;
  const ptt = { activeDirectId, txStatus, onPTTStart, onPTTEnd };
  const showTrails = settings?.show_trails !== false;

  return (
    <div data-testid={IDS.map.root} className="h-full rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
        <div>
          <div className="overline">Live Location · {units.filter(u => u.online).length} moving{showTrails ? " · trails at zoom 14+" : ""}</div>
          <h3 className="text-sm font-semibold">Field Positions</h3>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-slate-400">
          {useGoogle ? "GOOGLE MAPS" : "OPENSTREETMAP"} <MapPin className="h-4 w-4" />
        </div>
      </div>
      <div className="relative flex-1 min-h-0">
        {units.length === 0 ? (
          <div className="absolute inset-0 grid place-items-center text-xs text-slate-400"><span><Radio className="inline h-4 w-4 mr-1" />No unit positions yet</span></div>
        ) : useGoogle ? (
          <APIProvider apiKey={settings.google_maps_key}>
            <GMap style={{ width: "100%", height: "100%" }} defaultCenter={units[0] ? { lat: units[0].lat, lng: units[0].lng } : DEFAULT_CENTER} defaultZoom={13} mapId="DEMO_MAP_ID" disableDefaultUI gestureHandling="greedy">
              <GoogleUnits units={units} alertIds={alertIds} selected={selected} setSelected={setSelected} {...ptt} />
            </GMap>
          </APIProvider>
        ) : (
          <OsmMap units={units} alertIds={alertIds} showTrails={showTrails} {...ptt} />
        )}
      </div>
    </div>
  );
}
